import {
  Button,
  Dialog,
  IconButton,
  Typography,
  DialogProps,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Unstable_Grid2';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from '@/components/feedback/snackbar';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { FC, useState, useEffect } from 'react';
import moment, { Moment } from 'moment-timezone';
import axios from '@/utils/api/axios';
import { saveAs } from 'file-saver';
import { useCatchError } from '@/utils';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

interface Props extends DialogProps {
  title: string;
  open: boolean;
  onClose: VoidFunction;
}

const AccountingOrdersExport: FC<Props> = ({ title, open, onClose }) => {
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [startDate, setStartDate] = useState<Moment | null>(moment().utc().startOf('month'));
  const [endDate, setEndDate] = useState<Moment | null>(moment().utc().endOf('day'));
  const catchError = useCatchError();

  // Reset dates to initial values when modal opens
  useEffect(() => {
    if (open) {
      setStartDate(moment().utc().startOf('month'));
      setEndDate(moment().utc().endOf('day'));
    }
  }, [open]);

  function exportExcel() {
    if (!startDate || !endDate) {
      enqueueSnackbar(t('Enter a valid date'), {
        variant: 'error',
        autoHideDuration: 5000,
      });
    } else if (startDate.isAfter(endDate)) {
      enqueueSnackbar(t('The start date cannot be after the end date'), {
        variant: 'error',
        autoHideDuration: 5000,
      });
    } else {
      axios
        .get('orders/accounting-export', {
          params: { startDate: startDate.toDate(), endDate: endDate.toDate() },
          responseType: 'blob',
        })
        .then((response) => {
          saveAs(response.data, `accounting-${startDate.format('L')}-to-${endDate.format('L')}`);
          enqueueSnackbar(t('Export completed successfully'), {
            variant: 'success',
            autoHideDuration: 3000,
          });
        })
        .catch(catchError);

      onClose();
    }
  }

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
      <DialogTitle>{title}</DialogTitle>

      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 15,
          top: 15,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>
          {t('Select the range of dates')}
        </Typography>
        <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={i18n.language}>
          <Grid container spacing={3}>
            <Grid xs={12} md={6}>
              <DatePicker
                label={t('Start Date')}
                onChange={(newValue: Moment | null) => {
                  setStartDate(newValue?.utc().startOf('day') || null);
                }}
                value={startDate}
                maxDate={moment().utc().endOf('day')}
                format="L"
                sx={{ width: '100%' }}
              />
            </Grid>
            <Grid xs={12} md={6}>
              <DatePicker
                label={t('End Date')}
                onChange={(newValue: Moment | null) => {
                  setEndDate(newValue?.utc().endOf('day') || null);
                }}
                value={endDate}
                maxDate={moment().utc().endOf('day')}
                format="L"
                sx={{ width: '100%' }}
              />
            </Grid>
          </Grid>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={onClose} sx={{ mr: 1 }}>
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            exportExcel();
          }}
        >
          {t('Export')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AccountingOrdersExport;
